// src/securityFlow/EvaluationPanel.jsx
import React, { useMemo, useCallback, useEffect } from 'react';
import { Box, Text, Group, Stack, Paper, Select, MultiSelect, Textarea, Badge, SimpleGrid, Progress, Card } from '@mantine/core';
import { Navigation } from '../components/NavigationButtons';
import { useSelector } from 'react-redux';
import loadSecurityRisks from '../loadSecurityRisks';
import {
  IconUsers,
  IconShield,
  IconTarget,
  IconAlertTriangle,
  IconCheck,
} from '@tabler/icons-react';
import ScenarioActionPlan from './ScenarioActionPlan';
import { useDispatch } from 'react-redux';
import { requestScenarioName, saveScenario } from '../redux/actions/securityActions';

// Child component to avoid calling hooks inside map
function AttributeSelect({ attrName, attrDef, assessmentData, handleChange }) {
  const data = useMemo(() => {
    const rawOptions = Array.isArray(attrDef?.options) ? attrDef.options : [];
    return rawOptions.map((opt) => {
      if (typeof opt === 'string') return { value: opt, label: opt };
      if (opt && typeof opt === 'object') {
        const value = String(opt.value ?? opt.name ?? opt.label ?? JSON.stringify(opt));
        const label = String(opt.label ?? opt.name ?? opt.value ?? value);
        return { value, label };
      }
      return { value: String(opt), label: String(opt) };
    });
  }, [attrDef?.options]);
  
  const value = useMemo(() => {
    const rawVal = assessmentData?.['Scenario Attributes']?.[attrName];
    const normalized = Array.isArray(rawVal)
      ? rawVal.map((v) => String(v?.value ?? v))
      : [];
    return normalized.filter((v) => data.some((d) => d.value === v));
  }, [assessmentData, attrName, data]);
  
  const handleAttributeChange = useCallback((vals) => {
    console.log('[UI] Change attrs', { attrName, vals });
    handleChange('Scenario Attributes', attrName, vals);
  }, [attrName, handleChange]);
  
  return (
    <Box>
      <Text size="sm" fw={500} mb={4}>{attrName}</Text>
      <MultiSelect
        placeholder={`Select ${attrName.toLowerCase()}`}
        data={data}
        value={value}
        onChange={handleAttributeChange}
        searchable
        clearable
      />
    </Box>
  );
}

export default function EvaluationPanel({
  scenario,
  assessmentData,
  vulnerabilityCategories,
  handleChange,
  assessmentPhase,
  setAssessmentPhase,
  threatAgentsConfig,
  securityConfig,
  onUpdateScenarioAgents,
  onCompleteAssessment,
  // scenario action plan props
  modelId,
  assessmentUuid,
  securityControls,
  onRefreshScenarioControls,
  onSaveScenarioControl,
  isGeneratingActions = false,
}) {
  const dispatch = useDispatch();
  // Load security impact assessment config from state, fallback to local defaults
  const impactConfigFromState = useSelector((s) => s?.config?.configs?.securityImpactAssessment);
  const impactConfigRaw = impactConfigFromState || loadSecurityRisks();
  // Support both shapes: {confidentiality,...} or {securityImpactAssessment:{...}}
  const impactConfig =
    impactConfigRaw &&
    typeof impactConfigRaw === 'object' &&
    !Array.isArray(impactConfigRaw) &&
    impactConfigRaw.securityImpactAssessment &&
    typeof impactConfigRaw.securityImpactAssessment === 'object'
      ? impactConfigRaw.securityImpactAssessment
      : impactConfigRaw;
  // —— Null-safety; identical rendering
  const safeObj = (v) =>
    v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  const attackSurfaceData = useMemo(() => safeObj(
    assessmentData && Array.isArray(vulnerabilityCategories) && vulnerabilityCategories.length > 0
      ? (assessmentData[vulnerabilityCategories[0]] || {})
      : {}
  ), [assessmentData, vulnerabilityCategories]);

  // Get attack surface categories from config for initial rendering
  const attackSurfaceConfig = useMemo(() => {
    if (!Array.isArray(securityConfig) || !vulnerabilityCategories?.[0]) return null;
    return securityConfig.find(config => config.name === vulnerabilityCategories[0]) || null;
  }, [securityConfig, vulnerabilityCategories]);

  // Get available attack surface types for rendering; support both original and transformed configs
  const availableAttackTypes = useMemo(() => {
    if (!attackSurfaceConfig?.schema) return [];
    // Transformed schema from useMergedSecurityForm: type === 'multiselect' with options
    if (Array.isArray(attackSurfaceConfig.schema.options) && attackSurfaceConfig.schema.options.length > 0) {
      return attackSurfaceConfig.schema.options;
    }
    // Original schema: category with subcategories
    if (Array.isArray(attackSurfaceConfig.schema.subcategories)) {
      return attackSurfaceConfig.schema.subcategories.map(sub => sub.name);
    }
    return [];
  }, [attackSurfaceConfig]);

  const phaseInfo = {
    attributes: {
      title: 'Scenario Attributes',
      description: 'Select attributes that define this scenario',
      icon: <IconTarget size={20} />,
      color: 'teal',
      phase: 1,
    },
    'agent-mapping': {
      title: 'Threat Agent Mapping',
      description: 'Select relevant threat agents for this scenario',
      icon: <IconUsers size={20} />,
      color: 'blue',
      phase: 2,
    },
    'attack-surface': {
      title: 'Attack Surface Assessment',
      description: 'Evaluate attack vectors and vulnerabilities',
      icon: <IconShield size={20} />,
      color: 'orange',
      phase: 3,
    },
    impact: {
      title: 'Impact Assessment',
      description: 'Assess potential business impact (CIA)',
      icon: <IconAlertTriangle size={20} />,
      color: 'red',
      phase: 4,
    },
    'action-plan': {
      title: 'Action Plan',
      description: 'Generate and curate controls for this scenario',
      icon: <IconCheck size={20} />,
      color: 'blue',
      phase: 5,
    },
    naming: {
      title: 'Name & finalize',
      description: 'Accept or edit the suggested scenario name',
      icon: <IconCheck size={20} />,
      color: 'blue',
      phase: 5,
    },
    summary: {
      title: 'Scenario Summary',
      description: 'Overview of selections and outcomes',
      icon: <IconCheck size={20} />,
      color: 'green',
      phase: 5,
    },
  };

  const handleAgentToggle = (agentName) => {
    const currentAgents = Array.isArray(scenario?.mappedAgents) ? scenario.mappedAgents : [];
    const newAgents = currentAgents.includes(agentName)
      ? currentAgents.filter((a) => a !== agentName)
      : [...currentAgents, agentName];
    onUpdateScenarioAgents(scenario.id, newAgents);
  };

  const canProceedToNext = () => {
    if (assessmentPhase === 'agent-mapping') {
      const currentAgents = Array.isArray(scenario?.mappedAgents) ? scenario.mappedAgents : [];
      return currentAgents.length > 0;
    }
    if (assessmentPhase === 'attack-surface') {
      // Check if at least one attack type has been assessed
      return availableAttackTypes.some(attackType => {
        const data = attackSurfaceData[attackType];
        return data && (data['Technical Vulnerability'] || data['Process Strength']);
      });
    }
    return true;
  };

  const currentPhase = phaseInfo[assessmentPhase];
  // Naming step: fetch suggestion when entering naming phase
  const suggestedName = useSelector((s) => s.security.models[modelId]?.scenarios?.[scenario?.id]?.nameSuggestion);
  const namingStatus = useSelector((s) => s.security.models[modelId]?.scenarios?.[scenario?.id]?.namingStatus);
  useEffect(() => {
    if (assessmentPhase === 'naming' && scenario?.id && !suggestedName && namingStatus !== 'loading') {
      dispatch(requestScenarioName(modelId, scenario.id));
    }
  }, [assessmentPhase, scenario?.id, suggestedName, namingStatus, dispatch, modelId]);

  const [nameInput, setNameInput] = React.useState('');
  useEffect(() => {
    if (assessmentPhase === 'naming') setNameInput(suggestedName || scenario?.name || '');
  }, [assessmentPhase, suggestedName, scenario?.name]);

  return (
    <Stack gap="lg">
      {/* Progress Header */}
      <Box>
        <Group justify="space-between" mb="md">
          <Group gap="sm">
            {currentPhase.icon}
            <Box>
              <Text size="lg" fw={600} c="dark.7">
                {currentPhase.title}
              </Text>
              <Text size="sm" c="dimmed">
                {currentPhase.description}
              </Text>
            </Box>
          </Group>
          <Badge size="lg" variant="light" color={currentPhase.color} radius="sm">
            Step {currentPhase.phase} of 5
          </Badge>
        </Group>

        {/* Progress Bar */}
        <Paper withBorder radius="md" p="xs" mb="lg">
          <Progress
            value={(currentPhase.phase / 5) * 100}
            size="md"
            radius="md"
            color={currentPhase.color}
            striped
            animate
          />
        </Paper>

        {/* Scenario Context removed per UX request */}
      </Box>

      {/* Phase Content */}
      {assessmentPhase === 'attributes' && (
        <Card withBorder radius="md" p="xl">
          <Stack gap="lg">
            <SimpleGrid cols={2} spacing="lg">
              {Object.entries(threatAgentsConfig?.schema?.attributes || {}).map(([attrName, attrDef]) => (
                <AttributeSelect
                  key={attrName}
                  attrName={attrName}
                  attrDef={attrDef}
                  assessmentData={assessmentData}
                  handleChange={handleChange}
                />
              ))}
            </SimpleGrid>
          </Stack>
        </Card>
      )}

      {assessmentPhase === 'agent-mapping' && (
        <Card
          withBorder
          radius="md"
          p="xl"
          sx={(theme) => ({
            backgroundColor: theme.white,
            borderColor: theme.colors.gray[3],
          })}
        >
          <Stack gap="lg">
            {/* Agent Selection Grid */}
            <SimpleGrid cols={2} spacing="md">
              {(threatAgentsConfig?.schema?.options || []).map((agent) => {
                const currentAgents = Array.isArray(scenario?.mappedAgents) ? scenario.mappedAgents : [];
                const isSelected = currentAgents.includes(agent);
                return (
                  <Paper
                    key={agent}
                    p="md"
                    radius="md"
                    withBorder
                    onClick={() => handleAgentToggle(agent)}
                    sx={(theme) => ({
                      cursor: 'pointer',
                      borderColor: isSelected ? theme.colors.blue[4] : theme.colors.gray[3],
                      backgroundColor: isSelected ? theme.colors.blue[0] : theme.white,
                      borderWidth: isSelected ? 2 : 1,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: isSelected ? theme.colors.blue[5] : theme.colors.gray[4],
                        transform: 'translateY(-1px)',
                        boxShadow: theme.shadows.sm,
                      },
                    })}
                  >
                    <Group justify="space-between">
                      <Text size="sm" fw={500} c={isSelected ? 'blue.7' : 'dark.7'}>
                        {agent}
                      </Text>
                      {isSelected && <IconCheck size={18} style={{ color: '#1c7ed6' }} />}
                    </Group>
                  </Paper>
                );
              })}
            </SimpleGrid>

            {/* Selected Summary */}
            {Array.isArray(scenario?.mappedAgents) && scenario.mappedAgents.length > 0 && (
              <Paper
                p="md"
                radius="md"
                sx={(theme) => ({
                  backgroundColor: theme.colors.blue[0],
                  border: `1px solid ${theme.colors.blue[2]}`,
                })}
              >
                <Group gap="xs">
                  <IconCheck size={18} style={{ color: '#1c7ed6' }} />
                  <Text size="sm" fw={500} c="blue.7">
                    {scenario.mappedAgents.length} agent
                    {scenario.mappedAgents.length > 1 ? 's' : ''} selected
                  </Text>
                </Group>
              </Paper>
            )}
          </Stack>
        </Card>
      )}

      {assessmentPhase === 'attack-surface' && attackSurfaceConfig && (
        <Stack gap="md" mt="md">
          {availableAttackTypes.map((attackType, index) => {
            const attackData = attackSurfaceData[attackType] || {};
            return (
            <Card
              key={attackType}
              withBorder
              radius="md"
              p="xl"
              sx={(theme) => ({
                backgroundColor: theme.white,
                borderColor: theme.colors.gray[3],
              })}
            >
              <Group gap="sm" mb="xs">
                <Box>
                  <Text size="sm" fw={600} c="dark.7">
                    {attackType}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Attack vector {index + 1}
                  </Text>
                </Box>
              </Group>

              <SimpleGrid cols={2} spacing="lg">
                <Select
                  label={
                    <Text size="sm" fw={500} c="dark.7" mb={4}>
                      Technical Vulnerability
                    </Text>
                  }
                  placeholder="Select vulnerability level"
                  data={[
                    { value: 'None', label: 'None - No vulnerability' },
                    { value: 'Negligible', label: 'Negligible - Very minor' },
                    { value: 'Low', label: 'Low - Some exposure' },
                    { value: 'Moderate', label: 'Moderate - Notable risk' },
                    { value: 'Significant', label: 'Significant - High exposure' },
                    { value: 'High', label: 'High - Major vulnerability' },
                    { value: 'Critical', label: 'Critical - Severe risk' },
                  ]}
                  value={attackData?.['Technical Vulnerability'] || ''}
                  onChange={(value) =>
                    handleChange(vulnerabilityCategories[0], attackType, {
                      ...(attackData || {}),
                      'Technical Vulnerability': value,
                    })
                  }
                />

                <Select
                  label={
                    <Text size="sm" fw={500} c="dark.7" mb={4}>
                      Process Strength
                    </Text>
                  }
                  placeholder="Select process maturity"
                  data={[
                    { value: 'Excellent', label: 'Excellent - Best practice' },
                    { value: 'Very Good', label: 'Very Good - Strong controls' },
                    { value: 'Good', label: 'Good - Adequate controls' },
                    { value: 'Fair', label: 'Fair - Basic controls' },
                    { value: 'Poor', label: 'Poor - Weak controls' },
                    { value: 'Unsatisfactory', label: 'Unsatisfactory - No controls' },
                  ]}
                  value={attackData?.['Process Strength'] || ''}
                  onChange={(value) =>
                    handleChange(vulnerabilityCategories[0], attackType, {
                      ...(attackData || {}),
                      'Process Strength': value,
                    })
                  }
                />
              </SimpleGrid>

              <Box mt="lg">
                <Text size="sm" fw={500} c="dark.7" mb={4}>
                  Additional Considerations
                </Text>
                <Textarea
                  placeholder="Document specific vulnerabilities, existing controls, or other relevant factors..."
                  value={attackData?.details || ''}
                  onChange={(e) =>
                    handleChange(vulnerabilityCategories[0], attackType, {
                      ...(attackData || {}),
                      details: e.currentTarget.value,
                    })
                  }
                  autosize
                  minRows={2}
                  maxRows={4}
                />
              </Box>
            </Card>
            );
          })}
        </Stack>
      )}

      {assessmentPhase === 'impact' && (
        <Stack gap="md">
          {['confidentiality', 'integrity', 'availability'].map((key) => {
            const current = assessmentData?.['Scenario Impact']?.[key] || [];
            const options = (impactConfig?.[key]) || [];
            return (
              <Card key={key} withBorder radius="md" p="xl" mb="md" sx={(t) => ({ backgroundColor: t.white, borderColor: t.colors.gray[3] })}>
                <Text size="md" fw={600} c="dark.7" mb="sm">{key.charAt(0).toUpperCase() + key.slice(1)}</Text>
                <Stack gap="sm">
                  <MultiSelect
                    placeholder={`Select ${key} impact attributes`}
                    data={options.map((o) => ({ value: o, label: o }))}
                    value={current}
                    onChange={(value) => handleChange('Scenario Impact', key, value)}
                    searchable
                    clearable
                  />
                  <Textarea
                    placeholder={`Rationale for ${key} impact (optional)`}
                    value={assessmentData?.['Scenario Impact']?.[`${key}_rationale`] || ''}
                    onChange={(e) => handleChange('Scenario Impact', `${key}_rationale`, e.currentTarget.value)}
                    autosize minRows={2} maxRows={4}
                  />
                </Stack>
              </Card>
            );
          })}
        </Stack>
      )}

      {assessmentPhase === 'action-plan' && (
        <ScenarioActionPlan
          modelId={modelId}
          assessmentUuid={assessmentUuid}
          scenarioId={scenario?.id}
          controlsByScenario={securityControls}
          onSaveControl={onSaveScenarioControl}
          onRefresh={onRefreshScenarioControls}
          hideHeader
          isGenerating={isGeneratingActions}
        />
      )}

      {assessmentPhase === 'naming' && (
        <Card withBorder radius="md" p="xl">
          <Stack gap="sm">
            <Text size="md" fw={600} c="dark.7">Name & finalize</Text>
            <Text size="sm" c="dimmed">We suggest a concise name for this scenario. You can accept or edit.</Text>
            <Textarea
              value={nameInput}
              onChange={(e) => setNameInput(e.currentTarget.value)}
              autosize minRows={1} maxRows={3}
            />
            <Group gap="sm">
              <button
                onClick={async () => {
                  const finalName = (nameInput || '').trim() || scenario?.name || 'Scenario';
                  await dispatch(saveScenario({ modelId, scenarioId: scenario.id, name: finalName }));
                  onCompleteAssessment({ ...scenario, name: finalName }, { showSummary: true });
                  setAssessmentPhase('summary');
                }}
              >
                Accept & Save
              </button>
            </Group>
          </Stack>
        </Card>
      )}

      {assessmentPhase === 'summary' && (
        <Stack gap="md">
          <Card withBorder radius="md" p="xl">
            <Text size="lg" fw={700} c="dark.7">{scenario?.name || 'Scenario Summary'}</Text>
            <Text size="sm" c="dimmed">Overview of your selections</Text>
          </Card>
          <Card withBorder radius="md" p="xl">
            <Text size="sm" fw={600} c="dark.7" mb="xs">Threat Agents</Text>
            <Text size="sm" c="dimmed">{Array.isArray(scenario?.mappedAgents) && scenario.mappedAgents.length ? scenario.mappedAgents.join(', ') : 'None'}</Text>
          </Card>
          <Card withBorder radius="md" p="xl">
            <Text size="sm" fw={600} c="dark.7" mb="xs">Scenario Attributes</Text>
            <Stack gap="xs">
              {Object.entries(assessmentData?.['Scenario Attributes'] || {}).map(([k,v]) => (
                <Group key={k} gap="sm"><Text size="sm" fw={500}>{k}:</Text><Text size="sm" c="dimmed">{Array.isArray(v)? v.join(', '): String(v||'')}</Text></Group>
              ))}
            </Stack>
          </Card>
          <Card withBorder radius="md" p="xl">
            <Text size="sm" fw={600} c="dark.7" mb="xs">Attack Surface</Text>
            <Stack gap="xs">
              {Object.entries(assessmentData?.[vulnerabilityCategories?.[0]] || {}).map(([attack, data]) => (
                <Group key={attack} gap="sm">
                  <Text size="sm" fw={500}>{attack}:</Text>
                  <Text size="sm" c="dimmed">{data?.['Technical Vulnerability'] || '–'} / {data?.['Process Strength'] || '–'}</Text>
                </Group>
              ))}
            </Stack>
          </Card>
          <Card withBorder radius="md" p="xl">
            <Text size="sm" fw={600} c="dark.7" mb="xs">Impact</Text>
            <Stack gap="xs">
              {Object.entries(assessmentData?.['Scenario Impact'] || {}).filter(([k]) => !k.endsWith('_rationale')).map(([k,v]) => (
                <Group key={k} gap="sm"><Text size="sm" fw={500}>{k}:</Text><Text size="sm" c="dimmed">{Array.isArray(v)? v.join(', '): String(v||'')}</Text></Group>
              ))}
            </Stack>
          </Card>
        </Stack>
      )}

      {/* Navigation */}
      <Box mt="md">
        <Group justify="space-between" mb="xs">
          <Text size="sm" c="dimmed">
            {assessmentPhase === 'agent-mapping' && (!Array.isArray(scenario?.mappedAgents) || scenario.mappedAgents.length === 0) && 'Select at least one agent'}
            {assessmentPhase === 'attack-surface' && !canProceedToNext() && 'Complete at least one vector assessment'}
            {assessmentPhase === 'impact' && 'Ready to continue'}
          </Text>
        </Group>
        <Navigation
          onBack={() => {
            if (assessmentPhase === 'attack-surface') setAssessmentPhase('attributes');
            else if (assessmentPhase === 'impact') setAssessmentPhase('attack-surface');
            else if (assessmentPhase === 'action-plan') setAssessmentPhase('impact');
            else if (assessmentPhase === 'summary') setAssessmentPhase('action-plan');
          }}
          onNext={() => {
            if (assessmentPhase === 'attributes') setAssessmentPhase('agent-mapping');
            else if (assessmentPhase === 'agent-mapping') setAssessmentPhase('attack-surface');
            else if (assessmentPhase === 'attack-surface') setAssessmentPhase('impact');
            else if (assessmentPhase === 'impact') setAssessmentPhase('action-plan');
            else if (assessmentPhase === 'action-plan') setAssessmentPhase('naming');
            else onCompleteAssessment(scenario, { close: true });
          }}
          nextLabel={assessmentPhase === 'summary' ? 'Complete Assessment' : 'Next Step'}
          nextDisabled={!canProceedToNext()}
          showBack={assessmentPhase !== 'attributes'}
        />
      </Box>
    </Stack>
  );
}
